/**
 * Runtime configuration for RuleEngine UI
 * Priority: Vite env (build-time) > window.RuleEngineConfig (runtime) > defaults
 * 
 * This allows the same frontend build to work across different environments:
 * - Development: Uses Vite env variables
 * - Docker: Uses Vite env variables
 * - Production: Uses runtime config from /public/config.js or defaults
 */

const DEFAULT_EMBED_PATH = '/demo/';

const ensureTrailingSlash = (value) => (value.endsWith('/') ? value : `${value}/`);

const isUnsetEmbedBase = (value, origin) => {
    if (!value || value === '/') return true;
    try {
        const parsed = new URL(value, origin || undefined);
        const path = parsed.pathname || '/';
        return path === '/' && (!origin || parsed.origin === origin);
    } catch {
        return false;
    }
};

/**
 * Resolve where copied embed snippets should load the configurator from.
 * Relative "/" and origin-root URLs are ignored so host pages do not iframe Studio.
 * Absolute CDN URLs are kept as-is. Relative paths are joined to the current origin.
 */
export const resolveEmbedBaseUrl = (
    viteValue = import.meta.env.VITE_EMBED_BASE_URL,
    runtimeValue = typeof window !== 'undefined' ? window.RuleEngineConfig?.embedBaseUrl : undefined,
    origin = typeof window !== 'undefined' ? window.location.origin : ''
) => {
    const raw = !isUnsetEmbedBase(viteValue, origin)
        ? viteValue
        : (!isUnsetEmbedBase(runtimeValue, origin) ? runtimeValue : '');

    if (!raw) {
        return origin + DEFAULT_EMBED_PATH;
    }

    if (/^https?:\/\//i.test(raw)) {
        return ensureTrailingSlash(raw);
    }

    const path = raw.startsWith('/') ? raw : `/${raw}`;
    return origin + ensureTrailingSlash(path);
};

export const getConfig = () => ({
  // API base - relative path through gateway
  apiBase: import.meta.env.VITE_API_BASE || 
            (window.RuleEngineConfig?.apiBase) || 
            '/api',
  
  embedBaseUrl: resolveEmbedBaseUrl(),
  
  environment: import.meta.env.VITE_ENVIRONMENT ||
               (window.RuleEngineConfig?.environment) ||
               'development'
});

export const getApiUrl = (path) => {
  const config = getConfig();
  
  // If path already starts with apiBase, return it as-is
  if (path.startsWith(config.apiBase)) {
    return path;
  }
  
  // Remove leading / from path if present
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  // Ensure apiBase ends with /
  const baseWithSlash = config.apiBase.endsWith('/') ? config.apiBase : config.apiBase + '/';
  return baseWithSlash + normalizedPath;
};

export const getEmbedUrl = (path = '') => {
  const config = getConfig();
  return config.embedBaseUrl + (path ? path : '');
};

export const joinEmbedTokenUrl = (base, token) => {
  const trimmed = (base || '').replace(/\/+$/, '');
  return `${trimmed}/?token=${encodeURIComponent(token)}`;
};

export const withEmbedFlag = (url) => {
  if (!url) return '';
  if (/(?:\?|&)embed=/.test(url)) return url;
  return url.includes('?') ? `${url}&embed=1` : `${url}?embed=1`;
};

export const joinEmbedSnippetUrl = (base, token) => withEmbedFlag(joinEmbedTokenUrl(base, token));

export const isLocalEmbedTestingHost = () => {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

export const getLocalEmbedHostUrl = (token) => {
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:8088/?token=${encodeURIComponent(token)}&embed=1`;
};

export const getDemoSandboxUrl = (token) => {
  if (!token || typeof window === 'undefined') return '';
  return `${window.location.origin}/demo/?token=${encodeURIComponent(token)}`;
};
